import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth, API } from "../App";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors, closestCorners,
} from "@dnd-kit/core";
import {
  SortableContext, verticalListSortingStrategy, useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Loader2, Globe, MapPin, Plus, Search, Calendar,
  X, FileText, Hotel, Camera, CreditCard, User, Zap,
  Filter, Tag, ArrowUpDown, Check, AlertCircle,
  Waves, Mountain, TreePine, Users, Building2, ChevronDown,
} from "lucide-react";
import { PIPELINE_DOT_COLORS, PIPELINE_STATUS_LABELS } from "../lib/constants";
import SlideOverPanel from "../components/SlideOverPanel";
import TradeFairCapture from "../components/TradeFairCapture";

const PRIORITY_DOT = { low: "bg-white/20", medium: "bg-blue-400", high: "bg-orange-400", urgent: "bg-red-400" };
const FACILITY_ICONS = { spa: "S", golf: "G", beach: "B", ski: "K", mice_capable: "M", meeting_rooms: "R" };

// ─── Prospect Card ───
function ProspectCard({ deal, onClick, isDragging }) {
  const primaryContact = (deal.contacts || []).find(c => c.is_primary) || (deal.contacts || [])[0];
  const contactName = primaryContact?.name || deal.primary_contact_name;
  const nextAction = (deal.next_actions || []).find(a => a.status === "open" && a.date);
  const nextDate = deal.next_action_date;
  const facilities = ["spa","golf","beach","ski","mice_capable","meeting_rooms"].filter(f => deal[f]);

  return (
    <div
      className={`p-3 rounded-lg bg-white/[0.04] border border-white/[0.06] cursor-grab active:cursor-grabbing transition-all ${isDragging ? "shadow-xl shadow-[#C9A96E]/10 opacity-80 scale-[1.02]" : "hover:bg-white/[0.07] hover:border-white/[0.1]"}`}
      onClick={onClick}
      data-testid={`pipeline-card-${deal.id}`}
    >
      <div className="flex items-start gap-2 mb-1">
        {deal.logo_url ? (
          <img src={deal.logo_url} alt="" className="w-5 h-5 rounded object-cover mt-0.5 shrink-0" />
        ) : (
          <Hotel className="w-4 h-4 text-white/20 mt-0.5 shrink-0" />
        )}
        <p className="text-white text-sm font-medium leading-tight flex-1 line-clamp-2">{deal.title}</p>
        {deal.production_review_pending && (
          <span className="relative flex shrink-0" data-testid={`review-badge-${deal.id}`}>
            <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse" />
            <span className="w-2 h-2 rounded-full bg-red-500" />
          </span>
        )}
      </div>
      {deal.production_review_pending && (
        <p className="text-red-400/70 text-[9px] font-bold tracking-wider uppercase ml-6 mb-0.5" data-testid={`review-label-${deal.id}`}>Production review pending</p>
      )}
      {(deal.location || deal.city) && (
        <div className="flex items-center gap-1.5 text-white/40 text-xs mb-0.5 ml-6">
          <MapPin className="w-3 h-3 shrink-0" />
          <span className="truncate">{deal.location || [deal.city, deal.country].filter(Boolean).join(", ")}</span>
        </div>
      )}
      {deal.consortium && (
        <div className="flex items-center gap-1.5 text-white/40 text-xs mb-0.5 ml-6">
          <Building2 className="w-3 h-3 shrink-0" />
          <span className="truncate">{deal.consortium}</span>
        </div>
      )}
      {contactName && (
        <div className="flex items-center gap-1.5 text-white/40 text-xs mb-0.5 ml-6">
          <span className="w-3 h-3 rounded-full bg-white/10 shrink-0" />
          <span className="truncate">{contactName}</span>
        </div>
      )}
      {facilities.length > 0 && (
        <div className="flex flex-wrap gap-0.5 ml-6 mb-0.5">
          {facilities.map(f => (
            <span key={f} className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400/70 font-medium">
              {FACILITY_ICONS[f]}
            </span>
          ))}
        </div>
      )}
      {(deal.tags || []).length > 0 && (
        <div className="flex flex-wrap gap-0.5 ml-6 mb-0.5">
          {deal.tags.slice(0, 3).map(t => <span key={t} className="text-[9px] px-1 py-0 rounded bg-white/[0.05] text-white/30">{t}</span>)}
          {deal.tags.length > 3 && <span className="text-[9px] text-white/20">+{deal.tags.length - 3}</span>}
        </div>
      )}
      <div className="flex items-center justify-between mt-1.5 pt-1.5 border-t border-white/5 ml-6">
        <div className="flex items-center gap-2">
          <div className={`w-1.5 h-1.5 rounded-full ${PRIORITY_DOT[deal.priority] || PRIORITY_DOT.medium}`} />
          {deal.value_estimate > 0 && (
            <span className="text-[#C9A96E] text-xs font-mono">{"\u20AC"}{deal.value_estimate.toLocaleString()}</span>
          )}
          {deal.import_batch && (
            <span className="text-[9px] px-1 rounded bg-violet-500/10 text-violet-400/60">{deal.import_batch}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {deal.proposals_count > 0 && <FileText className="w-3 h-3 text-orange-400" />}
          {(nextAction || nextDate) && (
            <span className="flex items-center gap-1 text-white/30 text-[10px]">
              <Calendar className="w-2.5 h-2.5" />
              {new Date((nextAction?.date || nextDate) + "T00:00").toLocaleDateString("de-DE", { day: "2-digit", month: "short" })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Sortable Card Wrapper ───
function SortableProspectCard({ deal, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: deal.id, data: { deal } });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <ProspectCard deal={deal} onClick={() => !isDragging && onClick(deal)} isDragging={isDragging} />
    </div>
  );
}

// ─── Quick Add Input (Asana-style + URL support) ───
function QuickAddCard({ status, onAddName, onAddUrl, urlLoading }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const ref = useRef(null);

  const isUrl = (str) => /^(https?:\/\/|www\.)/.test(str.trim()) || (str.includes(".") && !str.includes(" ") && str.length > 4);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    if (isUrl(value)) {
      let url = value.trim();
      if (!url.startsWith("http")) url = "https://" + url;
      onAddUrl(url);
    } else {
      onAddName(value.trim());
    }
    setValue("");
    setOpen(false);
  };

  if (!open) {
    return (
      <button
        onClick={() => { setOpen(true); setTimeout(() => ref.current?.focus(), 50); }}
        className="w-full py-2 text-white/25 hover:text-white/50 text-xs flex items-center justify-center gap-1.5 rounded-lg hover:bg-white/[0.04] transition-all group"
        data-testid={`quick-add-${status}`}
      >
        <Plus className="w-3.5 h-3.5 group-hover:text-[#C9A96E] transition-colors" /> Add Hotel
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="p-2 bg-white/[0.04] rounded-lg border border-[#C9A96E]/20" data-testid={`quick-add-form-${status}`}>
      <input
        ref={ref}
        value={value}
        onChange={e => setValue(e.target.value)}
        placeholder="Hotel name or website URL..."
        className="w-full bg-transparent text-white text-sm placeholder:text-white/25 outline-none mb-2"
        onKeyDown={e => e.key === "Escape" && setOpen(false)}
        data-testid={`quick-add-input-${status}`}
      />
      <div className="flex items-center gap-1.5">
        <button type="submit" disabled={urlLoading} className="px-2.5 py-1 bg-[#C9A96E]/20 text-[#C9A96E] text-xs rounded hover:bg-[#C9A96E]/30 transition-colors flex items-center gap-1 disabled:opacity-30" data-testid={`quick-add-submit-${status}`}>
          {urlLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
          {isUrl(value) ? "Fetch & Add" : "Add"}
        </button>
        <button type="button" onClick={() => { setOpen(false); setValue(""); }} className="px-2 py-1 text-white/30 text-xs hover:text-white/50">Cancel</button>
        {isUrl(value) && <span className="text-[10px] text-[#C9A96E]/60 ml-auto">URL detected</span>}
      </div>
    </form>
  );
}

// ─── Pipeline Column ───
function PipelineColumn({ status, label, deals, color, onCardClick, onQuickAddName, onQuickAddUrl, urlLoading }) {
  const isDiscoverColumn = status === "discover";
  return (
    <div className="flex flex-col min-w-[220px] max-w-[260px] shrink-0" data-testid={`pipeline-column-${status}`}>
      <div className="flex items-center gap-2 mb-2 px-1">
        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
        <span className="text-white/60 text-[11px] font-semibold uppercase tracking-wider">{label}</span>
        <span className="ml-auto text-white/20 text-xs font-mono">{deals.length}</span>
      </div>
      <div className="flex-1 bg-white/[0.015] rounded-xl p-2 space-y-2 min-h-[120px] border border-white/[0.03]">
        <SortableContext items={deals.map(d => d.id)} strategy={verticalListSortingStrategy}>
          {deals.map(deal => (
            <SortableProspectCard key={deal.id} deal={deal} onClick={onCardClick} />
          ))}
        </SortableContext>
        {deals.length === 0 && (
          <div className="flex items-center justify-center h-12 text-white/10 text-xs">Drop here</div>
        )}
        {isDiscoverColumn && (
          <QuickAddCard status={status} onAddName={onQuickAddName} onAddUrl={onQuickAddUrl} urlLoading={urlLoading} />
        )}
      </div>
    </div>
  );
}

// ─── Smart Intake Bar (URL / Name / Business Card) ───
function ProspectIntake({ onSubmitUrl, onQuickAdd, onSubmitCard, loading }) {
  const [mode, setMode] = useState("text"); // "text" | "card"
  const [value, setValue] = useState("");
  const [cardFront, setCardFront] = useState(null);
  const [cardBack, setCardBack] = useState(null);
  const [cardName, setCardName] = useState("");
  const [contactName, setContactName] = useState("");
  const [contactNote, setContactNote] = useState("");
  const frontRef = useRef(null);
  const backRef = useRef(null);

  const isUrl = (str) => /^(https?:\/\/|www\.)/.test(str.trim()) || (str.includes(".") && !str.includes(" ") && str.length > 5);

  const handleTextSubmit = (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    if (isUrl(value)) {
      let url = value.trim();
      if (!url.startsWith("http")) url = "https://" + url;
      onSubmitUrl(url);
    } else {
      onQuickAdd(value.trim());
    }
    setValue("");
  };

  const handleCardSubmit = (e) => {
    e.preventDefault();
    if (!cardName.trim()) { toast.error("Hotel name is required"); return; }
    onSubmitCard({ hotelName: cardName.trim(), contactName: contactName.trim(), contactNote: contactNote.trim(), front: cardFront, back: cardBack });
    setCardName(""); setContactName(""); setContactNote(""); setCardFront(null); setCardBack(null);
  };

  const detected = value.trim() && isUrl(value);

  return (
    <div className="mb-5" data-testid="prospect-intake">
      {/* Mode Tabs */}
      <div className="flex items-center gap-1 mb-2">
        <button
          onClick={() => setMode("text")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${mode === "text" ? "bg-white/[0.06] text-white" : "text-white/25 hover:text-white/50"}`}
          data-testid="intake-mode-text"
        >
          <Globe className="w-3.5 h-3.5" /> URL / Name
        </button>
        <button
          onClick={() => setMode("card")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${mode === "card" ? "bg-white/[0.06] text-white" : "text-white/25 hover:text-white/50"}`}
          data-testid="intake-mode-card"
        >
          <CreditCard className="w-3.5 h-3.5" /> Business Card
        </button>
      </div>

      {/* Text/URL Input */}
      {mode === "text" && (
        <form onSubmit={handleTextSubmit} className="flex gap-2">
          <div className="flex-1 relative">
            <Plus className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/20" />
            <input
              value={value}
              onChange={e => setValue(e.target.value)}
              placeholder="Add hotel — paste URL or type name..."
              className="w-full bg-white/[0.03] border border-white/[0.06] text-white text-sm rounded-xl pl-9 pr-3 py-3 outline-none placeholder:text-white/25 focus:border-[#C9A96E]/30 focus:bg-white/[0.04] transition-all"
              data-testid="intake-input"
            />
            {detected && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-[#C9A96E]/70 flex items-center gap-1">
                <Globe className="w-3 h-3" />URL
              </span>
            )}
          </div>
          <button
            type="submit"
            disabled={loading || !value.trim()}
            className="px-5 py-3 bg-[#C9A96E] text-white text-sm rounded-xl hover:bg-[#C9A96E]/90 transition-colors disabled:opacity-30 flex items-center gap-2 shrink-0 font-medium"
            data-testid="intake-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : detected ? <Search className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
            {loading ? "Fetching..." : detected ? "Fetch & Add" : "Add"}
          </button>
        </form>
      )}

      {/* Business Card Upload */}
      {mode === "card" && (
        <form onSubmit={handleCardSubmit} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4" data-testid="intake-card-form">
          {/* Hotel Name */}
          <div className="mb-3">
            <input
              value={cardName}
              onChange={e => setCardName(e.target.value)}
              placeholder="Hotel name *"
              className="w-full bg-white/[0.03] border border-white/[0.08] text-white text-sm rounded-lg px-3 py-2.5 outline-none placeholder:text-white/25 focus:border-[#C9A96E]/30 transition-colors"
              data-testid="card-hotel-name"
            />
          </div>

          {/* Contact Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
            <input
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              placeholder="Contact name (optional)"
              className="bg-white/[0.03] border border-white/[0.06] text-white text-sm rounded-lg px-3 py-2 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors"
              data-testid="card-contact-name"
            />
            <input
              value={contactNote}
              onChange={e => setContactNote(e.target.value)}
              placeholder="Note, e.g. GM - met at ITB (optional)"
              className="bg-white/[0.03] border border-white/[0.06] text-white text-sm rounded-lg px-3 py-2 outline-none placeholder:text-white/20 focus:border-white/[0.12] transition-colors"
              data-testid="card-contact-note"
            />
          </div>

          {/* Card Uploads */}
          <div className="grid grid-cols-2 gap-3 mb-3">
            {/* Front */}
            <div>
              <input ref={frontRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => setCardFront(e.target.files?.[0] || null)} data-testid="card-front-input" />
              <button
                type="button"
                onClick={() => frontRef.current?.click()}
                className={`w-full aspect-[1.6] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 transition-all ${cardFront ? "border-[#C9A96E]/30 bg-[#C9A96E]/5" : "border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02]"}`}
                data-testid="card-front-btn"
              >
                {cardFront ? (
                  <div className="text-center">
                    <Camera className="w-5 h-5 text-[#C9A96E] mx-auto mb-1" />
                    <span className="text-[11px] text-[#C9A96E] font-medium block truncate max-w-[120px] px-2">{cardFront.name}</span>
                  </div>
                ) : (
                  <>
                    <Camera className="w-5 h-5 text-white/20" />
                    <span className="text-[11px] text-white/25 font-medium">Front</span>
                  </>
                )}
              </button>
            </div>
            {/* Back */}
            <div>
              <input ref={backRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={e => setCardBack(e.target.files?.[0] || null)} data-testid="card-back-input" />
              <button
                type="button"
                onClick={() => backRef.current?.click()}
                className={`w-full aspect-[1.6] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1.5 transition-all ${cardBack ? "border-[#C9A96E]/30 bg-[#C9A96E]/5" : "border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02]"}`}
                data-testid="card-back-btn"
              >
                {cardBack ? (
                  <div className="text-center">
                    <Camera className="w-5 h-5 text-[#C9A96E] mx-auto mb-1" />
                    <span className="text-[11px] text-[#C9A96E] font-medium block truncate max-w-[120px] px-2">{cardBack.name}</span>
                  </div>
                ) : (
                  <>
                    <Camera className="w-5 h-5 text-white/20" />
                    <span className="text-[11px] text-white/25 font-medium">Back</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading || !cardName.trim()}
            className="w-full px-4 py-2.5 bg-[#C9A96E] text-white text-sm rounded-xl hover:bg-[#C9A96E]/90 transition-colors disabled:opacity-30 flex items-center justify-center gap-2 font-medium"
            data-testid="card-submit-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            {loading ? "Uploading..." : "Add Hotel"}
          </button>
        </form>
      )}
    </div>
  );
}

// ─── Main Pipeline Page ───
export default function PipelinePage() {
  const { token } = useAuth();
  const [pipelineData, setPipelineData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeDeal, setActiveDeal] = useState(null);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [intakeLoading, setIntakeLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [tradeFairMode, setTradeFairMode] = useState(() => {
    try { return localStorage.getItem("trade_fair_mode") === "true"; } catch { return false; }
  });
  const [showCapture, setShowCapture] = useState(false);
  // Server-side filters
  const [filters, setFilters] = useState({ tag: "", source: "", priority: "", assigned_user: "" });
  // Client-side structured filters
  const [structuredFilters, setStructuredFilters] = useState({
    region: "", country: "", city: "", consortium: "", import_batch: "",
    hotel_category: "", location_type: "", spa: false, golf: false,
    beach: false, ski: false, mice_capable: false, meeting_rooms: false,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [allTags, setAllTags] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [filterOptions, setFilterOptions] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  useEffect(() => { fetchPipeline(); }, [filters]);

  // Fetch tags, users, and filter options
  useEffect(() => {
    const h = { Authorization: `Bearer ${token}` };
    axios.get(`${API}/tags`, { headers: h }).then(r => setAllTags(r.data)).catch(() => {});
    axios.get(`${API}/users`, { headers: h }).then(r => setAllUsers(r.data)).catch(() => setAllUsers([]));
    axios.get(`${API}/hotels/filter-options`, { headers: h }).then(r => setFilterOptions(r.data)).catch(() => {});
  }, [token]);

  // Handle ?open=dealId from calendar navigation
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && pipelineData) {
      const allDeals = Object.values(pipelineData.pipeline).flatMap(col => col.deals);
      const deal = allDeals.find(d => d.id === openId);
      if (deal) {
        setSelectedDeal(deal);
        setSearchParams({}, { replace: true });
      }
    }
  }, [pipelineData, searchParams]);

  const fetchPipeline = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.tag) params.set("tag", filters.tag);
      if (filters.source) params.set("source", filters.source);
      if (filters.priority) params.set("priority", filters.priority);
      if (filters.assigned_user) params.set("assigned_user", filters.assigned_user);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/pipeline${qs}`, { headers: { Authorization: `Bearer ${token}` } });
      setPipelineData(res.data);
      if (selectedDeal) {
        const allDeals = Object.values(res.data.pipeline).flatMap(col => col.deals);
        const updated = allDeals.find(d => d.id === selectedDeal.id);
        if (updated) setSelectedDeal(updated);
      }
    } catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  };

  const handleUrlSubmit = async (url) => {
    setIntakeLoading(true);
    try {
      await axios.post(`${API}/prospects/from-url`, { url }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prospect added!");
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create prospect");
    } finally { setIntakeLoading(false); }
  };

  const handleQuickAdd = async (name) => {
    try {
      await axios.post(`${API}/prospects/quick`, { hotel_name: name }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prospect added!");
      fetchPipeline();
    } catch { toast.error("Failed to create prospect"); }
  };

  const handleCardSubmit = async ({ hotelName, contactName, contactNote, front, back }) => {
    setIntakeLoading(true);
    try {
      const params = new URLSearchParams({ hotel_name: hotelName });
      if (contactName) params.set("contact_name", contactName);
      if (contactNote) params.set("contact_note", contactNote);
      const formData = new FormData();
      if (front) formData.append("front", front);
      if (back) formData.append("back", back);
      await axios.post(`${API}/prospects/from-card?${params.toString()}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      toast.success("Hotel with business card added!");
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally { setIntakeLoading(false); }
  };

  const handleUrlAddToColumn = async (url) => {
    setIntakeLoading(true);
    try {
      await axios.post(`${API}/prospects/from-url`, { url }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Prospect added!");
      fetchPipeline();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create prospect");
    } finally { setIntakeLoading(false); }
  };

  const handleDragStart = useCallback((event) => {
    const deal = event.active.data?.current?.deal;
    if (deal) setActiveDeal(deal);
  }, []);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    setActiveDeal(null);
    if (!over || !pipelineData) return;

    const dealId = active.id;
    const deal = active.data?.current?.deal;
    if (!deal) return;

    let targetStatus = null;
    for (const s of pipelineData.statuses) {
      const colDeals = pipelineData.pipeline[s]?.deals || [];
      if (over.id === s || colDeals.some(d => d.id === over.id)) {
        targetStatus = s;
        break;
      }
    }

    if (targetStatus && targetStatus !== deal.status) {
      setPipelineData(prev => {
        const newPipeline = { ...prev.pipeline };
        const oldCol = { ...newPipeline[deal.status] };
        oldCol.deals = oldCol.deals.filter(d => d.id !== dealId);
        newPipeline[deal.status] = oldCol;
        const newCol = { ...newPipeline[targetStatus] };
        newCol.deals = [...newCol.deals, { ...deal, status: targetStatus }];
        newPipeline[targetStatus] = newCol;
        return { ...prev, pipeline: newPipeline };
      });

      try {
        await axios.put(`${API}/pipeline/${dealId}/move?status=${targetStatus}`, {}, { headers: { Authorization: `Bearer ${token}` } });
        if (selectedDeal?.id === dealId) {
          setSelectedDeal(prev => ({ ...prev, status: targetStatus }));
        }
      } catch {
        toast.error("Failed to move");
        fetchPipeline();
      }
    }
  }, [pipelineData, token, selectedDeal]);

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>;
  if (!pipelineData) return null;

  // Client-side filtering for structured attributes
  const hasStructuredFilter = Object.entries(structuredFilters).some(([k, v]) => typeof v === "boolean" ? v : v !== "");

  const applyStructuredFilter = (deal) => {
    if (!hasStructuredFilter) return true;
    const sf = structuredFilters;
    if (sf.region && deal.region !== sf.region) return false;
    if (sf.country && deal.country !== sf.country) return false;
    if (sf.city && deal.city !== sf.city) return false;
    if (sf.consortium && deal.consortium !== sf.consortium) return false;
    if (sf.import_batch && deal.import_batch !== sf.import_batch) return false;
    if (sf.hotel_category && deal.hotel_category !== sf.hotel_category) return false;
    if (sf.location_type && deal.location_type !== sf.location_type) return false;
    if (sf.spa && !deal.spa) return false;
    if (sf.golf && !deal.golf) return false;
    if (sf.beach && !deal.beach) return false;
    if (sf.ski && !deal.ski) return false;
    if (sf.mice_capable && !deal.mice_capable) return false;
    if (sf.meeting_rooms && !deal.meeting_rooms) return false;
    return true;
  };

  // Build filtered pipeline
  const filteredPipeline = {};
  for (const s of pipelineData.statuses) {
    const col = pipelineData.pipeline[s];
    filteredPipeline[s] = {
      ...col,
      deals: col.deals.filter(applyStructuredFilter),
    };
  }

  const totalValue = Object.values(filteredPipeline).reduce(
    (sum, col) => sum + col.deals.reduce((s, d) => s + (d.value_estimate || 0), 0), 0
  );
  const totalDeals = Object.values(filteredPipeline).reduce((s, col) => s + col.deals.length, 0);
  const activeFilterCount = Object.values(filters).filter(v => v).length;
  const structuredFilterCount = Object.entries(structuredFilters).filter(([k, v]) => typeof v === "boolean" ? v : v !== "").length;
  const totalFilterCount = activeFilterCount + structuredFilterCount;

  const clearAllFilters = () => {
    setFilters({ tag: "", source: "", priority: "", assigned_user: "" });
    setStructuredFilters({
      region: "", country: "", city: "", consortium: "", import_batch: "",
      hotel_category: "", location_type: "", spa: false, golf: false,
      beach: false, ski: false, mice_capable: false, meeting_rooms: false,
    });
  };

  const selectCls = "bg-white/[0.04] border border-white/[0.08] text-white text-xs rounded-lg px-2.5 py-1.5 outline-none [&>option]:bg-[#0A0A0A]";
  const toggleCls = (active) => `px-2.5 py-1 rounded-lg text-[11px] font-medium border transition-all cursor-pointer ${active ? "bg-cyan-500/15 border-cyan-500/30 text-cyan-400" : "bg-white/[0.02] border-white/[0.06] text-white/25 hover:text-white/40"}`;

  return (
    <div className={`p-4 lg:p-6 transition-all ${selectedDeal ? "mr-[520px]" : ""}`} data-testid="pipeline-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white" data-testid="pipeline-title">Pipeline</h1>
          <p className="text-white/40 text-sm mt-0.5">
            {totalDeals} prospects {"\u00B7"} Value: <span className="text-[#C9A96E] font-mono">{"\u20AC"}{totalValue.toLocaleString()}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              const next = !tradeFairMode;
              setTradeFairMode(next);
              try { localStorage.setItem("trade_fair_mode", String(next)); } catch {}
            }}
            className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold transition-all border ${tradeFairMode ? "bg-[#C9A96E]/15 border-[#C9A96E]/30 text-[#C9A96E] shadow-sm shadow-[#C9A96E]/10" : "bg-white/[0.03] border-white/[0.06] text-white/30 hover:text-white/50 hover:border-white/[0.1]"}`}
            data-testid="trade-fair-toggle"
          >
            <Zap className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Trade Fair</span>
            <div className={`w-8 h-[18px] rounded-full relative transition-colors ${tradeFairMode ? "bg-[#C9A96E]/30" : "bg-white/10"}`}>
              <div className={`absolute top-[2px] w-[14px] h-[14px] rounded-full transition-all ${tradeFairMode ? "left-[17px] bg-[#C9A96E]" : "left-[2px] bg-white/30"}`} />
            </div>
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="mb-4" data-testid="pipeline-filters">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${totalFilterCount > 0 ? "bg-[#C9A96E]/10 border-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/[0.03] border-white/[0.06] text-white/30 hover:text-white/50"}`}
            data-testid="toggle-filters-btn"
          >
            <Filter className="w-3.5 h-3.5" />
            Filters {totalFilterCount > 0 && <span className="bg-[#C9A96E] text-white w-4 h-4 rounded-full text-[9px] flex items-center justify-center font-bold">{totalFilterCount}</span>}
          </button>
          {totalFilterCount > 0 && (
            <button onClick={clearAllFilters} className="text-white/20 text-xs hover:text-white/40 transition-colors" data-testid="clear-filters">
              Clear all
            </button>
          )}
        </div>

        {showFilters && (
          <div className="mt-3 bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 space-y-3">
            {/* Row 1: Basic filters */}
            <div className="flex flex-wrap gap-2" data-testid="basic-filters">
              <select value={filters.priority} onChange={e => setFilters(f => ({ ...f, priority: e.target.value }))} className={selectCls} data-testid="filter-priority">
                <option value="">All Priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
              <select value={filters.source} onChange={e => setFilters(f => ({ ...f, source: e.target.value }))} className={selectCls} data-testid="filter-source">
                <option value="">All Sources</option>
                <option value="manual">Manual</option>
                <option value="url">URL</option>
                <option value="import">Import</option>
                <option value="business_card">Business Card</option>
                <option value="qr_code">QR Code</option>
                <option value="image_url">Image URL</option>
                <option value="image_text">Image Text</option>
              </select>
              <select value={filters.tag} onChange={e => setFilters(f => ({ ...f, tag: e.target.value }))} className={selectCls} data-testid="filter-tag">
                <option value="">All Tags</option>
                {allTags.map(t => <option key={t.id || t.name} value={t.name}>{t.name}</option>)}
              </select>
              {allUsers.length > 1 && (
                <select value={filters.assigned_user} onChange={e => setFilters(f => ({ ...f, assigned_user: e.target.value }))} className={selectCls} data-testid="filter-user">
                  <option value="">All Users</option>
                  {allUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              )}
            </div>

            {/* Row 2: Geography */}
            {filterOptions && (
              <>
                <div className="border-t border-white/[0.04] pt-3">
                  <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold mb-2">Geography</div>
                  <div className="flex flex-wrap gap-2" data-testid="geo-filters">
                    <select value={structuredFilters.region} onChange={e => setStructuredFilters(f => ({ ...f, region: e.target.value }))} className={selectCls} data-testid="filter-region">
                      <option value="">All Regions</option>
                      {(filterOptions.regions || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={structuredFilters.country} onChange={e => setStructuredFilters(f => ({ ...f, country: e.target.value }))} className={selectCls} data-testid="filter-country">
                      <option value="">All Countries</option>
                      {(filterOptions.countries || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={structuredFilters.city} onChange={e => setStructuredFilters(f => ({ ...f, city: e.target.value }))} className={selectCls} data-testid="filter-city">
                      <option value="">All Cities</option>
                      {(filterOptions.cities || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>

                {/* Row 3: Source / Consortium */}
                <div className="border-t border-white/[0.04] pt-3">
                  <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold mb-2">Source & Type</div>
                  <div className="flex flex-wrap gap-2" data-testid="source-type-filters">
                    <select value={structuredFilters.consortium} onChange={e => setStructuredFilters(f => ({ ...f, consortium: e.target.value }))} className={selectCls} data-testid="filter-consortium">
                      <option value="">All Consortiums</option>
                      {(filterOptions.consortiums || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={structuredFilters.import_batch} onChange={e => setStructuredFilters(f => ({ ...f, import_batch: e.target.value }))} className={selectCls} data-testid="filter-import-batch">
                      <option value="">All Import Batches</option>
                      {(filterOptions.import_batches || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    <select value={structuredFilters.hotel_category} onChange={e => setStructuredFilters(f => ({ ...f, hotel_category: e.target.value }))} className={selectCls} data-testid="filter-hotel-category">
                      <option value="">All Categories</option>
                      {(filterOptions.hotel_categories || []).map(v => <option key={v} value={v}>{v}</option>)}
                    </select>
                    {(filterOptions.location_types || []).length > 0 && (
                      <select value={structuredFilters.location_type} onChange={e => setStructuredFilters(f => ({ ...f, location_type: e.target.value }))} className={selectCls} data-testid="filter-location-type">
                        <option value="">All Location Types</option>
                        {filterOptions.location_types.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    )}
                  </div>
                </div>

                {/* Row 4: Facilities */}
                <div className="border-t border-white/[0.04] pt-3">
                  <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold mb-2">Facilities</div>
                  <div className="flex flex-wrap gap-1.5" data-testid="facility-filters">
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, spa: !f.spa }))} className={toggleCls(structuredFilters.spa)} data-testid="filter-spa">Spa</button>
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, golf: !f.golf }))} className={toggleCls(structuredFilters.golf)} data-testid="filter-golf">Golf</button>
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, beach: !f.beach }))} className={toggleCls(structuredFilters.beach)} data-testid="filter-beach">Beach</button>
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, ski: !f.ski }))} className={toggleCls(structuredFilters.ski)} data-testid="filter-ski">Ski</button>
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, mice_capable: !f.mice_capable }))} className={toggleCls(structuredFilters.mice_capable)} data-testid="filter-mice">MICE</button>
                    <button type="button" onClick={() => setStructuredFilters(f => ({ ...f, meeting_rooms: !f.meeting_rooms }))} className={toggleCls(structuredFilters.meeting_rooms)} data-testid="filter-meeting-rooms">Meeting Rooms</button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Intake Bar */}
      <ProspectIntake onSubmitUrl={handleUrlSubmit} onQuickAdd={(name) => handleQuickAdd(name)} onSubmitCard={handleCardSubmit} loading={intakeLoading} />

      {/* Kanban Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-3 overflow-x-auto pb-6" data-testid="pipeline-board">
          {pipelineData.statuses.map(status => (
            <PipelineColumn
              key={status}
              status={status}
              label={pipelineData.labels[status]}
              deals={filteredPipeline[status]?.deals || []}
              color={PIPELINE_DOT_COLORS[status] || "#64748B"}
              onCardClick={(deal) => setSelectedDeal(deal)}
              onQuickAddName={handleQuickAdd}
              onQuickAddUrl={handleUrlAddToColumn}
              urlLoading={intakeLoading}
            />
          ))}
        </div>
        <DragOverlay>
          {activeDeal && <ProspectCard deal={activeDeal} isDragging />}
        </DragOverlay>
      </DndContext>

      {/* Slide-Over Panel */}
      {selectedDeal && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setSelectedDeal(null)} />
          <SlideOverPanel
            deal={selectedDeal}
            onClose={() => setSelectedDeal(null)}
            token={token}
            onUpdate={fetchPipeline}
          />
        </>
      )}

      {/* Trade Fair Mode — Floating Action Button */}
      {tradeFairMode && !showCapture && !selectedDeal && (
        <button
          onClick={() => setShowCapture(true)}
          className="fixed bottom-20 right-6 w-14 h-14 sm:w-16 sm:h-16 bg-[#C9A96E] rounded-2xl shadow-lg shadow-[#C9A96E]/30 flex items-center justify-center text-white hover:bg-[#C9A96E]/90 active:scale-90 transition-all z-30 group"
          data-testid="trade-fair-fab"
        >
          <Camera className="w-6 h-6 sm:w-7 sm:h-7 group-hover:scale-110 transition-transform" />
        </button>
      )}

      {/* Trade Fair Capture Modal */}
      {showCapture && (
        <TradeFairCapture
          token={token}
          onCreated={() => { setShowCapture(false); fetchPipeline(); }}
          onClose={() => setShowCapture(false)}
        />
      )}
    </div>
  );
}
